/*
 * Collection endless scrolling (sections/main-collection-product-grid.liquid, "Endless scrolling").
 *
 * <collection-endless-scroll> sits under .products-grid inside [data-render-grid]. When the
 * shopper is ~600px from its top it fetches the next paginate page of this section
 * (?page=N&section_id=...) and appends that page's cards. A category / filter / sort change
 * swaps [data-render-grid], which disconnects this element (aborting its request) and
 * connects a fresh one at page 1, so stale responses never land.
 * Without JS the element is never defined and the crawlable pagination links inside it show.
 */
(function () {
  const ENDLESS_SCRIPT_VERSION = 1;
  if (window.__collectionEndlessScrollVersion >= ENDLESS_SCRIPT_VERSION) return;
  window.__collectionEndlessScrollVersion = ENDLESS_SCRIPT_VERSION;

  // --- Pure helpers (exercised by scripts/collection-grid tests) ---

  const withSectionId = (url, sectionId) => {
    const [path, query = ''] = String(url).split('?');
    const params = new URLSearchParams(query);
    params.set('section_id', sectionId);
    return `${path}?${params.toString()}`;
  };

  /** Cards to append: never the promo tile (default view only, first batch) and never a repeat. */
  const pickNewCards = (seenUrls, cards) =>
    cards.filter((card) => {
      if (card.hasAttribute('data-collection-promo')) return false;
      const url = card.getAttribute('data-card-url');
      if (!url) return true;
      if (seenUrls.has(url)) return false;
      seenUrls.add(url);
      return true;
    });

  window.CollectionEndlessScroll = { withSectionId, pickNewCards };

  if (typeof document === 'undefined' || !window.customElements) return;

  // --- Behavior ---

  const TRIGGER_MARGIN = 600;
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const storageKey = () => `collection-endless:${location.pathname}${location.search}`;
  let restoreAttempted = false;

  const readSaved = () => {
    try {
      return JSON.parse(sessionStorage.getItem(storageKey()) || 'null');
    } catch (e) {
      return null;
    }
  };

  const isBackForward = () => {
    const nav = performance.getEntriesByType?.('navigation')?.[0];
    return nav?.type === 'back_forward';
  };

  class CollectionEndlessScroll extends HTMLElement {
    connectedCallback() {
      this.grid = this.parentElement?.querySelector('.products-grid');
      this.status = this.querySelector('[data-endless-status]');
      this.page = Number(this.dataset.page) || 1;
      this.nextUrl = this.dataset.nextUrl || '';
      this.loading = false;
      this.controller = null;
      this.seenUrls = new Set(
        [...(this.grid?.querySelectorAll('[data-card-url]') || [])].map((el) => el.getAttribute('data-card-url'))
      );

      this.querySelector('[data-endless-retry]')?.addEventListener('click', () => this.loadNext());
      this.querySelector('[data-endless-top]')?.addEventListener('click', () => this.returnToTop());

      this.onPageHide = () => this.save();
      window.addEventListener('pagehide', this.onPageHide);

      if (!this.grid || !this.nextUrl) {
        this.setState('end');
        return;
      }
      this.setState('idle');
      this.observer = new IntersectionObserver((entries) => {
        // After a failure only the Retry button asks again, so scrolling cannot hammer the server.
        if (entries[0].isIntersecting && this.dataset.state !== 'error') this.loadNext();
      }, {
        rootMargin: `0px 0px ${TRIGGER_MARGIN}px 0px`,
      });
      this.observer.observe(this);
      this.restore();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      window.removeEventListener('pagehide', this.onPageHide);
    }

    /** state: idle | loading | error | end */
    setState(state) {
      this.dataset.state = state;
    }

    announce(text) {
      if (!this.status) return;
      this.status.textContent = '';
      // New text node on the next frame so repeated identical messages are still read.
      requestAnimationFrame(() => (this.status.textContent = text));
    }

    nearTrigger() {
      return this.getBoundingClientRect().top < window.innerHeight + TRIGGER_MARGIN;
    }

    async loadNext({ quiet = false } = {}) {
      if (this.loading || !this.nextUrl) return false;
      this.loading = true;
      this.controller = new AbortController();
      this.setState('loading');

      try {
        const response = await fetch(withSectionId(this.nextUrl, this.dataset.sectionId), {
          signal: this.controller.signal,
          credentials: 'same-origin',
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (!this.isConnected) return false;

        const nextEl = doc.querySelector('collection-endless-scroll');
        const cards = pickNewCards(this.seenUrls, [...(doc.querySelector('.products-grid')?.children || [])]);
        cards.forEach((card) => {
          // Appended batches are below the fold: never eager / high priority.
          card.querySelectorAll('img').forEach((img) => {
            img.loading = 'lazy';
            img.removeAttribute('fetchpriority');
          });
          card.style.setProperty('--mcpg-card-delay', '0ms');
        });
        this.grid.append(...cards);

        this.page = Number(nextEl?.dataset.page) || this.page + 1;
        this.nextUrl = nextEl?.dataset.nextUrl || '';
        this.loading = false;

        window.mcpgInitCombinedListingCards?.();
        window.mcpgInitJudgeMeBadges?.();
        this.dispatchEvent(
          new CustomEvent('collection:batch-loaded', { bubbles: true, detail: { page: this.page, cards } })
        );

        if (!this.nextUrl) {
          this.finish();
          return true;
        }
        this.setState('idle');
        if (!quiet) this.announce(this.dataset.loadedText.replace('[count]', this.grid.querySelectorAll('[data-card-url]').length));
        // The trigger can still be in range after a short batch; keep going without a scroll.
        if (!quiet && this.nearTrigger()) requestAnimationFrame(() => this.loadNext());
        return true;
      } catch (error) {
        this.loading = false;
        if (error.name === 'AbortError' || !this.isConnected) return false;
        this.setState('error');
        this.announce(this.dataset.errorText);
        return false;
      }
    }

    finish() {
      this.nextUrl = '';
      this.observer?.disconnect();
      this.setState('end');
      this.announce(this.querySelector('[data-endless-end-text]')?.textContent.trim() || '');
    }

    returnToTop() {
      window.scrollTo({ top: 0, behavior: reducedMotion() ? 'auto' : 'smooth' });
      document.getElementById('MainContent')?.focus({ preventScroll: true });
    }

    save() {
      try {
        sessionStorage.setItem(storageKey(), JSON.stringify({ page: this.page, y: window.scrollY }));
      } catch (e) {
        /* storage unavailable: Back simply starts at the first batch */
      }
    }

    /** Browser Back from a product (no bfcache): reload the batches seen before, then the scroll spot. */
    async restore() {
      if (restoreAttempted) return;
      restoreAttempted = true;
      const saved = readSaved();
      if (!saved || !isBackForward() || saved.page <= this.page) return;

      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      while (this.page < saved.page && this.nextUrl) {
        if (!(await this.loadNext({ quiet: true }))) break;
      }
      window.scrollTo(0, saved.y || 0);
      if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
    }
  }

  if (!customElements.get('collection-endless-scroll')) {
    customElements.define('collection-endless-scroll', CollectionEndlessScroll);
  }
})();
